import {
  handleGetRequest,
  handlePatchRequest,
  handlePostRequest,
} from "./http.service";

// Mirrors mynda-api's complaint.model.ts. Same vocabulary as the agency
// complaints under /placements — deliberately, so staff learn one set of words.
export type ComplaintStatus =
  | "open"
  | "investigating"
  | "resolved"
  | "dismissed";

export type ComplaintSeverity = "low" | "medium" | "high" | "critical";

export const COMPLAINT_STATUSES: ComplaintStatus[] = [
  "open",
  "investigating",
  "resolved",
  "dismissed",
];

export const STATUS_LABELS: Record<ComplaintStatus, string> = {
  open: "Open",
  investigating: "Investigating",
  resolved: "Resolved",
  dismissed: "Dismissed",
};

export interface ComplaintUser {
  _id: string;
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
}

export interface ComplaintComment {
  body: string;
  authorName?: string;
  createdAt: string;
}

export interface UserComplaint {
  _id: string;
  description: string;
  complaintBy: ComplaintUser;
  user: ComplaintUser;
  status: ComplaintStatus;
  severity: ComplaintSeverity;
  category?: string;
  assignedTo?: { _id: string; firstName?: string; lastName?: string; email: string };
  resolution?: string;
  resolvedAt?: string;
  comments?: ComplaintComment[];
  createdAt: string;
  updatedAt: string;
}

export interface ComplaintsResponse {
  page: number;
  limit: number;
  total: number;
  data: UserComplaint[];
}

export interface ComplaintSummary {
  total: number;
  open: number;
  investigating: number;
  resolved: number;
  dismissed: number;
  unassigned: number;
  critical: number;
}

export const getAllComplaints = async (params?: {
  page?: number;
  limit?: number;
  status?: string;
  severity?: string;
  unassigned?: boolean;
}) => {
  const qs = new URLSearchParams();
  if (params?.page) qs.set("page", String(params.page));
  if (params?.limit) qs.set("limit", String(params.limit));
  if (params?.status) qs.set("status", params.status);
  if (params?.severity) qs.set("severity", params.severity);
  if (params?.unassigned) qs.set("unassigned", "true");
  const query = qs.toString() ? `?${qs}` : "";
  return handleGetRequest<ComplaintsResponse>(`/complaints${query}`);
};

export const getComplaintSummary = async () =>
  handleGetRequest<ComplaintSummary>("/complaints/summary");

/**
 * Moves a complaint along. Resolving one notifies the complainant, so the API
 * requires a resolution to accompany that particular status.
 */
export const updateComplaint = async (
  id: string,
  data: {
    status?: ComplaintStatus;
    severity?: ComplaintSeverity;
    resolution?: string;
  }
) =>
  handlePatchRequest<typeof data, UserComplaint>(`/complaints/${id}`, data);

export const addComplaintComment = async (id: string, body: string) =>
  handlePostRequest<{ body: string }, UserComplaint>(
    `/complaints/${id}/comments`,
    { body }
  );
